/* eslint-disable max-lines-per-function */

import { InputText } from "@bluframe/blublocks"
import Button from "components/buttons/Button"
import { type ChangeEventHandler, useCallback, useState } from "react"

import type { ComponentProps } from "."
import { useTriggerViewState } from "./hooks/useTriggerViewState"
import {
  DisabledReason,
  TriggerCard,
  TriggerError,
  TriggerFooter,
  TriggerHeader,
  TriggerHint,
  TriggerLi,
  TriggerMeta,
  TriggerStatus,
  TriggersUl,
  TriggerTitle,
  Wrapper
} from "./styled"

const parseKeywordsOverride = (value: string): string[] | undefined => {
  const keywords = value
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean)

  return keywords.length ? keywords : undefined
}

const JobTriggerPanel = ({
  discoveryIngest,
  followerSync,
  onRunDiscoveryIngest,
  onRunFollowerSync,
  prospectDefaults
}: ComponentProps) => {
  const now = new Date()
  const [keywordsOverride, setKeywordsOverride] = useState<string | null>(null)
  const defaultKeywordsValue = prospectDefaults?.defaultKeywords?.length
    ? prospectDefaults.defaultKeywords.join(", ")
    : ""

  const discoveryState = useTriggerViewState({
    actionLabel: "Run ingest",
    now,
    title: "Discovery ingest",
    trigger: discoveryIngest
  })

  const followerState = useTriggerViewState({
    actionLabel: "Run follower sync",
    now,
    title: "Follower sync",
    trigger: followerSync
  })

  const handleKeywordsOverrideChange: ChangeEventHandler<HTMLInputElement> =
    useCallback((event) => {
      setKeywordsOverride(event.target.value)
    }, [])

  const handleRunDiscoveryIngest = useCallback(() => {
    if (keywordsOverride === null) {
      onRunDiscoveryIngest(undefined)
      return
    }

    onRunDiscoveryIngest(parseKeywordsOverride(keywordsOverride))
  }, [keywordsOverride, onRunDiscoveryIngest])

  return (
    <Wrapper aria-label="Job triggers" role="region">
      <TriggersUl aria-label="Job triggers list">
        <TriggerLi aria-label="Discovery ingest job trigger">
          <TriggerCard>
            <TriggerHeader>
              <TriggerMeta>
                <TriggerTitle>{discoveryState.title}</TriggerTitle>

                {discoveryState.errorMessage ? (
                  <TriggerError>{discoveryState.statusText}</TriggerError>
                ) : (
                  <TriggerStatus>{discoveryState.statusText}</TriggerStatus>
                )}

                {discoveryState.disabledReason ? (
                  <DisabledReason>
                    {discoveryState.disabledReason}
                  </DisabledReason>
                ) : null}
              </TriggerMeta>

              <Button
                disabled={discoveryState.isDisabled}
                expanded
                label={discoveryState.actionLabel}
                loading={discoveryState.isRunning}
                onClick={handleRunDiscoveryIngest}
              />
            </TriggerHeader>

            <TriggerFooter>
              <InputText
                inputId="socials-discovery-ingest-keywords-override"
                label="Keywords override (comma-separated)"
                name="keywordsOverride"
                onChange={handleKeywordsOverrideChange}
                value={keywordsOverride ?? defaultKeywordsValue}
              />

              <TriggerHint>
                {
                  "Overrides our standard discovery keywords. Use a comma-separated list."
                }
              </TriggerHint>
            </TriggerFooter>
          </TriggerCard>
        </TriggerLi>

        <TriggerLi aria-label="Follower sync job trigger">
          <TriggerCard>
            <TriggerHeader>
              <TriggerMeta>
                <TriggerTitle>{followerState.title}</TriggerTitle>

                {followerState.errorMessage ? (
                  <TriggerError>{followerState.statusText}</TriggerError>
                ) : (
                  <TriggerStatus>{followerState.statusText}</TriggerStatus>
                )}

                {followerState.disabledReason ? (
                  <DisabledReason>
                    {followerState.disabledReason}
                  </DisabledReason>
                ) : null}
              </TriggerMeta>

              <Button
                disabled={followerState.isDisabled}
                expanded
                label={followerState.actionLabel}
                loading={followerState.isRunning}
                onClick={onRunFollowerSync}
              />
            </TriggerHeader>
          </TriggerCard>
        </TriggerLi>
      </TriggersUl>
    </Wrapper>
  )
}

export default JobTriggerPanel
